import React, { useState, useEffect } from 'react'
import { useAppSelector } from '../../hooks'
import RoomSelectionDialog from '../../components/RoomSelectionDialog'
import LoginDialog from '../../components/LoginDialog'
import { RootState } from '../../stores'
import { ServiceFactory } from '../factories/ServiceFactory'

interface RoomControllerProps {
  /** Whether to show the login dialog instead of room selection */
  showLogin?: boolean
}

/**
 * Modular Room Controller
 * Manages room selection and login dialogs using the new service architecture
 */
const RoomController: React.FC<RoomControllerProps> = ({
  showLogin = false,
}): JSX.Element | null => {
  const loadingGame = useAppSelector((state: RootState) => state.game.loadingGame)
  const loggedIn = useAppSelector((state: RootState) => state.user.loggedIn)
  const [isInitializing, setIsInitializing] = useState(true)

  // Get services from factory
  const workspaceService = ServiceFactory.getInstance().getWorkspaceService()

  // Load workspace settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Race against an 8-second timeout so a slow connection never keeps
        // the room controller in the initializing (null) state indefinitely.
        const timeout = new Promise<void>((resolve) => setTimeout(resolve, 8000))
        await Promise.race([workspaceService.ensureSettingsLoaded(), timeout])
      } catch (error) {
        console.error('Error loading workspace settings:', error)
      } finally {
        setIsInitializing(false)
      }
    }

    loadSettings()
  }, [workspaceService])

  // Don't render dialogs when game is loading or initializing
  if (loadingGame || isInitializing) return null

  // After loading completes, if user is already logged in, don't show any dialog
  if (loggedIn) return null

  // Show login or room selection dialog based on showLogin prop
  return showLogin ? <LoginDialog /> : <RoomSelectionDialog />
}

export default RoomController 